// src/oura/onboarding/formAnswers.ts
//
// The words and the sums on the Form answers screen (#4065). No React, so each
// of them can be read and checked on its own.

import type { FormResponseAttempt, FormResponseStatus, FormResponseSummary } from "@service/aeo/formResponses";
import { dayTime } from "./derive";

/** What the table shows: the two statuses a staffer has to act on, everything, or one status. */
export type FormAnswersView = "needs" | "all" | FormResponseStatus;

/** The `status` the list route is asked for. Undefined asks for every status. */
export function statusesFor(view: FormAnswersView): FormResponseStatus[] | undefined {
    if (view === "all") return undefined;
    return view === "needs" ? ["not_received", "unmatched"] : [view];
}

export const STATUS_LABEL: Record<FormResponseStatus, string> = {
    filed: "Filed",
    unmatched: "Matched no client",
    dismissed: "Dismissed",
    not_received: "Not received",
};

/** Success, warning and danger, on the house scale. */
export const STATUS_CHIP: Record<FormResponseStatus, string> = {
    filed: "border-emerald-200 bg-emerald-50 text-emerald-700",
    unmatched: "border-amber-200 bg-amber-50 text-amber-700",
    dismissed: "border-gray-200 bg-gray-50 text-gray-600",
    not_received: "border-rose-200 bg-rose-50 text-rose-700",
};

const SUMMARY_KEY: Record<FormResponseStatus, "filed" | "unmatched" | "dismissed" | "notReceived"> = {
    filed: "filed",
    unmatched: "unmatched",
    dismissed: "dismissed",
    not_received: "notReceived",
};

/** One count from the summary; `dismissed` is absent from a backend before #6454 and reads 0. */
const summaryCount = (s: FormResponseSummary, status: FormResponseStatus): number => s[SUMMARY_KEY[status]] ?? 0;

/** How many answers in the window the view holds, from the server's own summary. */
export function countFor(view: FormAnswersView, s: FormResponseSummary): number {
    if (view === "all") return s.total;
    if (view === "needs") return s.notReceived + s.unmatched;
    return summaryCount(s, view);
}

/**
 * The summary after one answer changed status on this screen: one fewer of
 * what it was, one more of what it is. The total does not move.
 */
export function summaryAfter(s: FormResponseSummary, from: FormResponseStatus, to: FormResponseStatus): FormResponseSummary {
    if (from === to) return s;
    return { ...s, [SUMMARY_KEY[from]]: Math.max(0, summaryCount(s, from) - 1), [SUMMARY_KEY[to]]: summaryCount(s, to) + 1 };
}

/** The first day of a window that ends today, as the YYYY-MM-DD the route takes. Local time. */
export function sinceDate(days: number, now = new Date()): string {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const SOURCE_WORDS: Record<string, string> = { script: "by the form", staff: "by staff" };
const OUTCOME_WORDS: Record<string, string> = {
    filed: "filed",
    unmatched: "matched no client",
    duplicate: "already in Oura",
    failed: "failed",
};

/** "6 Oct, 15:28 · by the form · failed". A source or outcome this screen has no word for is left out, never printed raw. */
export function attemptLine(a: FormResponseAttempt): string {
    return [dayTime(a.at), SOURCE_WORDS[a.source], OUTCOME_WORDS[a.outcome]].filter(Boolean).join(" · ");
}
